import * as signalR from "@microsoft/signalr";
import { filter } from "lodash";

import type { entityProcessConfig } from "../entity-process/signalr-process-config";
import { entityResultWithAccessValidator } from "../helper/custom-validator";
import { Enums } from "../helper/enums";
import { clientEntity } from "../model/entity/client-entity";
import { clientLocationEntity } from "../model/entity/client-location-entity";
import { contactEntity } from "../model/entity/contact-entity";
import { contactViewingEntity } from "../model/entity/contact-viewing-entity";
import { ticketEntity } from "../model/entity/ticket-entity";
import { SignalRPostActions } from "../services/post-services/signalr-post-action";
import { EntityManager } from "./entity-manager";
import { ErrorTemplateBuilder } from "../../infrastructure/error-template-builder";
import contactService from "./contact-service";
import { Contact } from "../model/crm/contact";
import PrototypeInstance from "../helper/prototype-instance";

//Ticket Functions

export class SignalRCRMService {
  //#region SignalR Connection
  public setCRMConnectionEvents(connection: signalR.HubConnection) {
    connection.on("contactEntitySync", async (message: any) => {
      try {
        await this.contactEntitySync(JSON.parse(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRCRMService.contactEntitySync",
            e,
          ),
        );
      }
    });

    connection.on("clientLocationEntitySync", async (message: any) => {
      try {
        await this.clientLocationEntitySync(JSON.parse(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRCRMService.clientLocationEntitySync",
            e,
          ),
        );
      }
    });
  }
  //#endregion

  //#region Functions

  public async clientEntitySync(message: any) {
    try {
      await EntityManager.updateEntity(
        message,
        Enums.SignalRMessageType[Enums.SignalRMessageType.ClientEntitySync],
        "",
        message.entityId,
      );
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRCRMService.clientEntitySync",
          e,
        ),
      );
    }
  }

  //Called when a sentinel integration is deleted, and clients are unmapped from sentinel sites
  public async clientEntityTickSync(message: any) {
    try {
      const latestTick = clientEntity.query<any>().max("tick");
      const [result, object]: [any, entityProcessConfig | null] =
        await EntityManager.getOnlyAPCallForEntity(
          message,
          Enums.SignalRMessageType[
            Enums.SignalRMessageType.ClientEntityTickSync
          ],
          latestTick,
        );
      if (entityResultWithAccessValidator(result, object)) {
        let activeClients = filter(result[0], ["clientStatusId", 1]);
        clientEntity.insertOrUpdate({ data: activeClients });
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRCRMService.clientEntityTickSync",
          e,
        ),
      );
    }
  }

  public async contactEntitySync(message: any) {
    try {
      let contactId: number =
        PrototypeInstance.globalProperties.$store.getters[
          "ContactModule/getContactId"
        ];
      if (contactId == message.entityId) {
        const contactList: Contact[] = await contactService.getPratialContactById(
          message.entityId,
        );
        if (contactList && contactList.length > 0) {
          localStorage.setItem("contact", JSON.stringify(contactList[0]));
          PrototypeInstance.globalProperties.$store.commit(
            "ContactModule/SET_CONTACT",
            contactList[0],
          );
        }
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalCRMService.contactEntitySync",
          e,
        ),
      );
    }
  }

  public async clientInactiveEntitySync(message: any) {
    let showSnackBar = false;
    try {
      if (EntityManager.checkEnumStateType(message)) {
        let client = clientEntity
          .query<any>()
          .where("clientId", message.entityId)
          .first();
        if (client != null) showSnackBar = true;

        clientEntity.delete([message.entityId]);

        contactViewingEntity.delete((contact: any) => {
          return contact.clientId == message.entityId;
        });

        contactEntity
          .delete((contact: any) => {
            return contact.clientId == message.entityId;
          })
          .catch((err: any) => {
            console.error("Error Occured:" + err);
          });

        await ticketEntity.delete((ticket: any) => {
          return ticket.clientId == message.entityId;
        });
        let payload = {
          isSuccess: true,
          message: "Client Inactivated successfully.",
          Timeout: 4000,
        };
        if (showSnackBar)
          PrototypeInstance.globalProperties.$storeInstance!.dispatch(
            "MasterModule/ShowSnackbar",
            payload,
          );

        void PrototypeInstance.globalProperties.$routeInstance!.push({
          path: "/crm/client-list",
        });
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalrCRMService.clientInactiveEntitySync",
          e,
        ),
      );
    }
  }

  public async contactEntityTickSync(message: any) {
    try {
      const latestTick = contactEntity.query().max("tick");
      const [result, object]: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          message,
          Enums.SignalRMessageType[
            Enums.SignalRMessageType.ContactEntityTickSync
          ],
          "",
          latestTick,
        );
      if (entityResultWithAccessValidator(result, object)) {
        SignalRPostActions.PostCallBackForEntity(
          Enums.SignalRMessageType.ContactEntityTickSync,
          result[0],
        );
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalCRMService.contactEntityTickSync",
          e,
        ),
      );
    }
  }

  public async contactInactiveEntitySync(message: any) {
    try {
      const [result, object]: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          message,
          Enums.SignalRMessageType[
            Enums.SignalRMessageType.ContactInactiveEntitySync
          ],
          "",
          message.entityId,
        );
      if (entityResultWithAccessValidator(result, object)) {
        SignalRPostActions.PostCallBackForEntity(
          Enums.SignalRMessageType.ContactInactiveEntitySync,
          result[0],
        );
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalCRMService.contactEntityTickSync",
          e,
        ),
      );
    }
  }

  public async clientLocationEntitySync(message: any) {
    try {
      await EntityManager.updateEntity(
        message,
        Enums.SignalRMessageType[
          Enums.SignalRMessageType.ClientLocationEntitySync
        ],
        "",
        message.entityId,
      );
      await contactService.setIsBillingContact();
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalCRMService.clientLocationEntitySync",
          e,
        ),
      );
    }
  }

  public async clientLocationUpdateEntitySync(message: any) {
    try {
      await EntityManager.updateEntity(
        message,
        Enums.SignalRMessageType[
          Enums.SignalRMessageType.ClientLocationUpdateEntitySync
        ],
        "",
        message.entityId,
      );
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalCRMService.clientLocationUpdateEntitySync",
          e,
        ),
      );
    }
  }

  public async clientLocationEntityTickSync(message: any) {
    try {
      const latestTick = clientLocationEntity.query<any>().max("tick");
      await EntityManager.updateEntity(
        message,
        Enums.SignalRMessageType[
          Enums.SignalRMessageType.ClientLocationEntityTickSync
        ],
        "",
        latestTick,
      );
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalCRMService.clientLocationEntityTickSync",
          e,
        ),
      );
    }
  }

  public async pax8ClientSyncSignalHandler(signalRMessage: any) {
    if (
      signalRMessage != null &&
      signalRMessage.clients != null &&
      signalRMessage.clients.length > 0
    ) {
      for (let mappedClient of signalRMessage.clients) {
        let client = clientEntity
          .query()
          .whereId(mappedClient.ClientId)
          .first();
        if (client != null) {
          await clientEntity.update({
            where: client.clientId,
            data: { pax8Id: mappedClient.Pax8Id },
          });
        }
      }

      let payload = {
        isSuccess: true,
        message: `${signalRMessage.clients.length} Client(s) synced with Pax8 successfully`,
        Timeout: 4000,
      };
      PrototypeInstance.globalProperties.$storeInstance!.dispatch(
        "MasterModule/ShowSnackbar",
        payload,
      );
    }
  }

  public async clientSubscribedGroupEntity(signalRMessage: any) {
    try {
      await EntityManager.updateEntity(
        signalRMessage,
        Enums.SignalRMessageType[
          Enums.SignalRMessageType.ClientSubscribedGroupEntity
        ],
        "",
        null,
      );
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalCRMService.clientSubscribedGroupEntity",
          e,
        ),
      );
    }
  }

  //::TO-DO [This API Change we need to send status from api for insert/delete, then we can get rid of status if/else]
  public contactSignalr(message: any) {
    try {
      if (message.eventId == Enums.StateEntitySync.InsertOrUpdate) {
        let messageText = "";
        if (message.statusId == 1) {
          let userSupportRequestsCount: number =
            PrototypeInstance.globalProperties.$storeInstance!.getters[
              "getUserSupportRequestsCount"
            ];
          userSupportRequestsCount += 1;
          PrototypeInstance.globalProperties.$storeInstance!.dispatch(
            "setUserSupportRequestsCount",
            userSupportRequestsCount,
          );
          messageText = "A new call request is added";
        } else if (message.statusId == 2) {
          messageText = "A call is being taken by a Technician.";
        } else if (message.statusId == 3) {
          messageText = "A call is ended.";
        }

        let payload = { isSuccess: true, message: messageText, Timeout: 4000 };
        PrototypeInstance.globalProperties.$storeInstance!.dispatch(
          "MasterModule/ShowSnackbar",
          payload,
        );
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText("SignalCRMService.contactSignalr", e),
      );
    }
  }

  //#endregion
}
